import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, userType, videStatusConst } from 'constant/const'
import axiosInstance from 'utils/axiosConfig'
import { toastError, toastSuccess } from 'utils/toastify'

const initialState = {
   allVideoData: [],
   videoList: [],
   loading: false,
   videoDetails: null,
   commentList: [],
   totalCommentsCount: 0,
   isNextCommentPage: null,
   videoLineChartData: null,
   videoSummary: '',
   videoContent: null,
   videoContentLoading: false,
   videoSummaryLoading: false,
   videoContentType: 'brandsafety',
   totalVideoCount: 0,
   isNextVideoPage: null,
   fetchVideoLoading: false,
   isFetchVideo: false,
   loadMoreComments: false,
   videoDetailsLoader: false,
   videoRiskScore: null,
   loadingComment: false,
   loadMoreCommentsLoading: false,
   updateCommentLoading: false,
   updateCommentStatus: false,
   isBatchAnalysisStatus: false,
   selectedVideoIds: [],
   isBackToVideo: false,
   isCallListAfterNotification: false,
   pageNumber: 1,
   is2000MoreCommentFlag: false
}

export const getAllVideoListAction = createAsyncThunk(
   'video/getAllVideoListAction',
   async (data) => {
      try {
         const params = new URLSearchParams(data).toString()
         const res = await axiosInstance.get(`/yt/get_videos_list/?${params}`)
         return res.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getVideoDetailsByIdAction = createAsyncThunk(
   'video/getVideoDetailsByIdAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/yt/get_video_by_id/?channel_id=${data?.channel_id}&video_id=${data?.video_id}&analyze=${data?.analyze}`
         )
         if (res.data?.data?.status === videStatusConst.processing) {
            toastSuccess(
               `Analyzing comments for ${res.data?.data?.title}. This may take a moment due to a high number of comments.`
            )
         }
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getCommentListAction = createAsyncThunk('video/getCommentListAction', async (data) => {
   try {
      const params = new URLSearchParams(data).toString()
      const res = await axiosInstance.get(`/yt/get_comments_list/?${params}`)
      return res.data
   } catch (error) {
      toastError(errorMessage)
      throw error.response ? error.response?.data?.message : error?.message
   }
})

export const getVideoLineChartAction = createAsyncThunk(
   'video/getVideoLineChartAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/yt/get_chart_data_by_video_id/?channel_id=${data?.channel_id}&video_id=${data?.video_id}&period_type=${data?.period_type}`
         )
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getVideoSummaryAction = createAsyncThunk(
   'video/getVideoSummaryAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/yt/get_video_summary_by_video_id/?channel_id=${data?.channel_id}&video_id=${data?.video_id}`
         )
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getVideoContentAction = createAsyncThunk(
   'video/getVideoContentAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/yt/get_content_analysis_by_video_id/?channel_id=${data?.channel_id}&video_id=${data?.video_id}&content_param=${data?.content_param}`
         )
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)
export const fetchNewVideoAction = createAsyncThunk('video/fetchNewVideoAction', async (data) => {
   try {
      const res = await axiosInstance.get(
         `/yt/fetch_videos/?channel_id=${data?.channel_id}&start_date=${data?.start_date}&end_date=${data?.end_date}`
      )
      return res.data?.data
   } catch (error) {
      toastError(errorMessage)
      throw error.response ? error.response?.data?.message : error?.message
   }
})
export const loadMoreCommentsAction = createAsyncThunk(
   'video/loadMoreCommentsAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/yt/get_more_comments/?channel_id=${data?.channel_id}&video_id=${data?.video_id}&offset=${data?.offset}`
         )
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const updateCommentsAction = createAsyncThunk('video/updateCommentsAction', async (data) => {
   try {
      const res = await axiosInstance.post(`/yt/user_update_comment/`, data)
      return res.data?.data
   } catch (error) {
      toastError(errorMessage)
      throw error.response ? error.response?.data?.message : error?.message
   }
})

// export const saveAnalysisAction = createAsyncThunk('video/saveAnalysisAction', async (data) => {
//    try {
//       const res = await axiosInstance.post(`/yt/user_update_comment/`, data)
//       return res.data?.data
//    } catch (error) {
//       toastError(errorMessage)
//       throw error.response ? error.response?.data?.message : error?.message
//    }
// })

const videoSlice = createSlice({
   name: 'video',
   initialState,
   reducers: {
      setVideoContentType(state, action) {
         state.videoContentType = action.payload
      },
      onResetVideoCount(state) {
         state.totalVideoCount = 0
         state.isNextVideoPage = null
         state.videoList = []
         state.videoDetails = null
         state.allVideoData = []
      },
      setVideoRiskScore(state, action) {
         state.videoRiskScore = action.payload
      },
      resetLoadMoreCommentsStatus(state) {
         state.loadMoreComments = false
      },
      resetVideoDetails(state) {
         state.videoDetails = null
      },
      resetUpdateCommentStatus(state) {
         state.updateCommentStatus = false
      },
      setBatchAnalysisStatus(state, action) {
         state.isBatchAnalysisStatus = action.payload
      },
      toggleVideoSelection: (state, action) => {
         const videoId = action.payload
         if (state.selectedVideoIds.includes(videoId)) {
            state.selectedVideoIds = state.selectedVideoIds.filter((id) => id !== videoId)
         } else {
            state.selectedVideoIds.push(videoId)
         }
      },
      clearAllSelections: (state) => {
         state.selectedVideoIds = []
      },
      toggleVideoList(state) {
         state.isCallListAfterNotification = !state.isCallListAfterNotification
      },
      setPageNumberAction(state, action) {
         state.pageNumber = action.payload
      },
      setAllVideoDataAction(state, action) {
         state.allVideoData = action.payload
      },
      resetIsBackToVideo(state) {
         state.isBackToVideo = false
      }
   },
   extraReducers: (builder) => {
      builder
         .addCase(getAllVideoListAction.pending, (state) => {
            state.loading = true
            state.commentList = []
            state.totalCommentsCount = 0
            state.videoLineChartData = null
            state.videoSummary = ''
            state.videoContent = null
            state.isFetchVideo = false
            state.videoDetails = null
            state.loadMoreComments = false
            state.isBackToVideo = false
            state.isCallListAfterNotification = false
         })
         .addCase(getAllVideoListAction.fulfilled, (state, action) => {
            state.loading = false
            state.videoList = action.payload?.data
            state.totalVideoCount = action.payload?.count
            state.isNextVideoPage = action.payload?.next
            state.isFetchVideo = false
            state.isBackToVideo = false
            state.isCallListAfterNotification = false
         })
         .addCase(getAllVideoListAction.rejected, (state) => {
            state.loading = false
            state.videoList = []
            state.isNextVideoPage = null
            state.isBackToVideo = false
         })
         .addCase(getVideoDetailsByIdAction.pending, (state) => {
            state.videoLineChartData = null
            state.videoSummary = ''
            state.videoContent = null
            state.isNextCommentPage = null
            state.commentList = []
            state.videoDetailsLoader = true
            state.videoDetails = null
            state.loadMoreComments = false
         })
         .addCase(getVideoDetailsByIdAction.fulfilled, (state, action) => {
            state.videoDetailsLoader = false
            if (action?.payload?.status === videStatusConst.processing) {
               state.isBackToVideo = true
            } else {
               state.isBackToVideo = false
               state.videoDetails = action.payload
            }
         })
         .addCase(getVideoDetailsByIdAction.rejected, (state) => {
            state.videoDetailsLoader = false
            state.videoDetails = null
            state.isBackToVideo = false
         })
         .addCase(getCommentListAction.pending, (state) => {
            state.loadingComment = true
            // state.commentList = []
            state.totalCommentsCount = 0
         })
         .addCase(getCommentListAction.fulfilled, (state, action) => {
            state.loadingComment = false
            state.commentList = action.payload?.data
            state.totalCommentsCount = action.payload?.count
            state.isNextCommentPage = action.payload?.next
         })
         .addCase(getCommentListAction.rejected, (state) => {
            state.loadingComment = false
            state.commentList = null
            state.totalCommentsCount = 0
            state.isNextCommentPage = null
         })
         .addCase(getVideoLineChartAction.pending, (state) => {
            state.loading = true
            state.videoLineChartData = null
         })
         .addCase(getVideoLineChartAction.fulfilled, (state, action) => {
            state.loading = false
            state.videoLineChartData = action.payload
         })
         .addCase(getVideoLineChartAction.rejected, (state) => {
            state.loading = false
            state.videoLineChartData = null
         })
         .addCase(getVideoSummaryAction.pending, (state) => {
            state.videoSummaryLoading = true
            state.videoSummary = ''
         })
         .addCase(getVideoSummaryAction.fulfilled, (state, action) => {
            state.videoSummaryLoading = false
            state.videoSummary = action.payload
         })
         .addCase(getVideoSummaryAction.rejected, (state) => {
            state.videoSummaryLoading = false
            state.videoSummary = ''
         })
         .addCase(getVideoContentAction.pending, (state) => {
            state.videoContentLoading = true
            state.videoContent = null
         })
         .addCase(getVideoContentAction.fulfilled, (state, action) => {
            state.videoContentLoading = false
            state.videoContent = action.payload
         })
         .addCase(getVideoContentAction.rejected, (state) => {
            state.videoContentLoading = false
            state.videoContent = null
         })
         .addCase(fetchNewVideoAction.pending, (state) => {
            state.fetchVideoLoading = true
            state.isFetchVideo = false
         })
         .addCase(fetchNewVideoAction.fulfilled, (state) => {
            state.fetchVideoLoading = false
            state.isFetchVideo = true
         })
         .addCase(fetchNewVideoAction.rejected, (state) => {
            state.fetchVideoLoading = false
            state.isFetchVideo = false
         })
         .addCase(loadMoreCommentsAction.pending, (state) => {
            state.loadMoreCommentsLoading = true
            state.loadMoreComments = false
         })
         .addCase(loadMoreCommentsAction.fulfilled, (state) => {
            state.loadMoreCommentsLoading = false
            state.loadMoreComments = true
            state.videoRiskScore = null
         })
         .addCase(loadMoreCommentsAction.rejected, (state) => {
            state.loadMoreCommentsLoading = false
            state.loadMoreComments = false
         })
         .addCase(updateCommentsAction.pending, (state) => {
            state.updateCommentLoading = true
            state.updateCommentStatus = false
         })
         .addCase(updateCommentsAction.fulfilled, (state, action) => {
            state.updateCommentLoading = false
            state.videoDetails = action.payload
            state.updateCommentStatus = true
         })
         .addCase(updateCommentsAction.rejected, (state) => {
            state.updateCommentLoading = false
            state.updateCommentStatus = false
         })
   }
})

export const {
   setVideoContentType,
   onResetVideoCount,
   setVideoRiskScore,
   resetLoadMoreCommentsStatus,
   resetVideoDetails,
   resetUpdateCommentStatus,
   setBatchAnalysisStatus,
   toggleVideoSelection,
   clearAllSelections,
   toggleVideoList,
   setPageNumberAction,
   setAllVideoDataAction,
   resetIsBackToVideo
} = videoSlice.actions
export default videoSlice.reducer
